import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import classes from "./quickSearchDropdown.module.scss";
import { postReq } from "../utils/request";
import { recordVisit } from "../utils/recentlyViewed";
import { COMMAND_PALETTE_HINT, COMMAND_PALETTE_KEYS, openCommandPalette } from "../utils/shortcuts";
import SearchSummaryBanner from "../components/SearchSummaryBanner";
import SearchSuggestions from "../components/SearchSuggestions";
import SearchResultRow from "../components/search/SearchResultRow";
import { useAiAccess } from "../hooks/useAiAccess";
import { useAiCapabilities } from "../hooks/useAiCapabilities";
import { useUserPermissions } from "../contexts/UserPermissionsContext";
import { looksLikeQuestion, openAssistant, openForm } from "../utils/assistant";
import { AskAction, matchActions, messageCost, topActions } from "../utils/askActions";
import { resumeAsk, waitingNote } from "../utils/askReturn";
import { useAskWaiting } from "../hooks/useAskWaiting";
import {
    CATEGORY_CONFIG,
    CATEGORY_ORDER,
    SearchResponseBody,
    SearchResultItem,
    SearchSuggestion,
    SearchSummary,
    subgroupResults,
} from "../constants/searchCategories";

const RESULTS_ID = "header-search-results";

/** One row above or below the records: an action to start, or asking CRX. */
interface CommandRow {
    key: string;
    kind: "do" | "ask";
    title: string;
    detail: string;
    icon: string;
    hint: string;
    run: () => void;
}

/**
 * Header search: type-ahead across every module. Results open in a new tab;
 * Enter with nothing selected (or "See all results") goes to /search.
 * For roles with AI on it is also where you ask CRX: an "Ask CRX" row (first
 * and selected when the text reads like a question, last otherwise) and a gold
 * button in the field open the assistant beside the page with the question sent.
 *
 * "Do or ask" (roadmap #31): typed words are matched by code to things the
 * person's role lets them start ("New task", "Ask for leave" …) — free, no
 * message used — and those come first when the words read as doing, with
 * "Fill it in with Ask" (one message) after them. Shift+Enter opens Ask with
 * the words in the box, unsent. An empty box (AI on) offers Open Ask, the
 * person's top actions and starter questions.
 */
const SearchDropdown: React.FC = () => {
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState("");
    const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
    const [summary, setSummary] = useState<SearchSummary | null>(null);
    const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    // The row picked with the arrow keys or the mouse; null = the default
    // (the first row above the records, if any), derived on every render so
    // fresh results never inherit an old pick.
    const [pickedIndex, setPickedIndex] = useState<number | null>(null);
    const resultRefs = useRef<(HTMLElement | null)[]>([]);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { access: aiAccess } = useAiAccess();
    const canAsk = !!aiAccess?.available;
    const { waiting } = useAskWaiting();
    const { currentUser } = useUserPermissions() as any;
    const userPages = useMemo(() => currentUser?.pages ?? [], [currentUser]);
    // The empty-box panel: Open Ask, the person's actions, starter questions.
    const [showStart, setShowStart] = useState(false);
    const capabilities = useAiCapabilities(null, canAsk && showStart);

    const normalizePhoneNumber = (phone: string) => phone.replace(/\D/g, "");

    const performSearch = useCallback(async (value: string) => {
        const isPhoneNumber = /^\+?\d{1,4}[\s()\-\d]*$/.test(value);
        const searchValue = isPhoneNumber ? normalizePhoneNumber(value) : value;

        if (searchValue.length >= 3) {
            setIsLoading(true);
            try {
                // Results and completions for the word being typed, side by side.
                const [response, suggested] = await Promise.all([
                    postReq<SearchResponseBody>("extra/quick-search", { text: searchValue, includeInactive: true }),
                    postReq<{ suggestions: SearchSuggestion[] }>("extra/search-suggest", { text: value }).catch(() => null),
                ]);
                setSearchResults(response.body.data || []);
                setPickedIndex(null);
                setSummary(response.body.summary ?? null);
                setSuggestions(suggested?.status === 200 ? (suggested.body?.suggestions ?? []) : []);
                setShowSearchResults(true);
            } catch (err) {
                console.error("Search failed", err);
                setSearchResults([]);
                setSummary(null);
                setSuggestions([]);
            } finally {
                setIsLoading(false);
            }
        } else {
            setSearchResults([]);
            setSummary(null);
            setSuggestions([]);
            setShowSearchResults(false);
        }
    }, []);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);
        setSearchResults([]);
        setPickedIndex(null);
        setShowStart(canAsk && value.length === 0);

        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        debounceTimer.current = setTimeout(() => {
            performSearch(value);
        }, 300);
    };

    /** Run a query handed over by a suggestion, a widen button or a chip's ×. */
    const pickQuery = (text: string) => {
        setSearchText(text);
        setPickedIndex(null);
        performSearch(text);
    };

    const handleViewAll = () => {
        setShowSearchResults(false);
        navigate(`/search?q=${encodeURIComponent(searchText.trim())}`);
    };

    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowSearchResults(false);
                setShowStart(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Inactive (soft-deleted) records render in their own section at the bottom;
    // flatResults must mirror the RENDERED order so keyboard navigation lines up.
    const activeResults = searchResults.filter((item) => !item.inactive);
    const inactiveResults = searchResults.filter((item) => item.inactive);

    // Each category is split under its "why" sub-headings (full matches /
    // offers X elsewhere / related to Y without X / name only).
    const groupedResults = CATEGORY_ORDER
        .map((category) => ({
            category,
            config: CATEGORY_CONFIG[category],
            subgroups: subgroupResults(activeResults.filter((item) => item.type === category)),
        }))
        .filter((group) => group.subgroups.some((sub) => sub.items.length > 0));

    const flatResults = [
        ...groupedResults.flatMap((group) => group.subgroups.flatMap((sub) => sub.items)),
        ...inactiveResults,
    ];

    const query = searchText.trim();

    /** Close the panel and empty the box (after a row did its thing). */
    const reset = () => {
        setSearchText("");
        setSearchResults([]);
        setSummary(null);
        setSuggestions([]);
        setShowSearchResults(false);
        setShowStart(false);
        setPickedIndex(null);
    };

    /** Ask CRX: the assistant opens beside this page with the question already sent. */
    const handleAsk = () => {
        if (!query) return;
        openAssistant(query, { send: true });
        reset();
    };

    /** Shift+Enter: Ask opens with the words in its box, unsent (no message used). */
    const handleAskUnsent = () => {
        openAssistant(query || undefined);
        reset();
    };

    /** A "do" row: open the form empty or go to the create page — no AI. */
    const runAction = (action: AskAction) => {
        if (action.form) openForm(action.form);
        else if (action.start) action.start();
        else if (action.to) navigate(action.to);
        reset();
    };

    const doRow = (action: AskAction): CommandRow => ({
        key: `do:${action.key}`,
        kind: "do",
        title: action.label,
        detail: action.note ?? "Opens the form · no AI credit used",
        icon: action.icon,
        hint: action.note ? action.hint : `${action.hint}. Nothing is saved until you press Save.`,
        run: () => runAction(action),
    });

    // Typed words: actions matched by code, then where Ask goes.
    const { actions: matched, isAction } = matchActions(query, userPages);
    const askVisible = canAsk && query.length >= 3;
    const fillsForm = isAction && matched.some((action) => action.fillable);
    const askRow: CommandRow | null = askVisible ? {
        key: "ask",
        kind: "ask",
        title: fillsForm ? "Fill it in with Ask" : "Ask CRX",
        detail: `“${query}” · ${messageCost(aiAccess)}`,
        icon: "stars",
        hint: fillsForm
            ? "Ask fills the form in from your words; you check it and save"
            : "Ask CRX this question: the answer opens beside this page",
        run: handleAsk,
    } : null;
    const actionRows = matched.map(doRow);
    const askFirst = !!askRow && !isAction && (looksLikeQuestion(query) || flatResults.length === 0);
    // Doing words put the actions (free) first, then "Fill it in with Ask"; a
    // question puts Ask first; a plain search keeps both after the records.
    const topRows: CommandRow[] = isAction
        ? [...actionRows, ...(askRow ? [askRow] : [])]
        : askFirst && askRow ? [askRow] : [];
    const bottomRows: CommandRow[] = isAction
        ? []
        : askFirst ? actionRows : [...actionRows, ...(askRow ? [askRow] : [])];
    // Doing words or a question with no records found: the record search's
    // "understood / no matches" notes are noise next to the rows above.
    const quietSearch = flatResults.length === 0 && (isAction || (askRow !== null && looksLikeQuestion(query)));
    const resultOffset = topRows.length;
    const totalRows = topRows.length + flatResults.length + bottomRows.length;
    const selectedIndex = pickedIndex ?? (topRows.length > 0 ? 0 : -1);
    const commandAt = (index: number): CommandRow | null => {
        if (index >= 0 && index < topRows.length) return topRows[index];
        const bottom = index - topRows.length - flatResults.length;
        return bottom >= 0 && bottom < bottomRows.length ? bottomRows[bottom] : null;
    };

    // The empty box: Open Ask (free), the person's top actions, starter questions.
    const questions = (capabilities?.areas ?? []).flatMap((area) => area.capabilities).slice(0, 3);
    const startRows: CommandRow[] = !canAsk ? [] : [
        // Ask waiting on an answer: back to that same answer, not a new chat
        waiting ? {
            key: "resume", kind: "ask", title: "Back to Ask", detail: `${waiting.title ? `${waiting.title} · ` : ""}${waitingNote(waiting)}`,
            icon: "stars", hint: "Opens the same answer, not a new chat · no AI credit used",
            run: () => { resumeAsk(); reset(); },
        } : {
            key: "open", kind: "ask", title: "Open Ask", detail: "No AI credit used until you send a question",
            icon: "stars", hint: "Open the assistant beside this page",
            run: () => { openAssistant(); reset(); },
        },
        ...topActions(userPages, capabilities).map(doRow),
        ...questions.map((item): CommandRow => ({
            key: `q:${item.key}`,
            kind: "ask",
            title: item.label,
            detail: item.send ? messageCost(aiAccess) : "Puts the question in Ask for you to finish · no message yet",
            icon: "chat-left-text",
            hint: item.question,
            run: () => { openAssistant(item.question, item.send ? { send: true } : {}); reset(); },
        })),
    ];
    const startVisible = showStart && !query && startRows.length > 0;
    const startIndex = pickedIndex ?? 0;

    /**
     * Opening a result from here feeds the Ctrl+K palette's Recent list too, so
     * "the thing I was just looking at" is one keystroke away regardless of which
     * search surface found it.
     */
    const rememberResult = (item: SearchResultItem) => {
        recordVisit({
            type: item.type,
            id: item.id,
            label: item.name || item.referenceNumber || "(untitled)",
            sublabel: item.parent || undefined,
            url: item.url,
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
            setShowSearchResults(false);
            setShowStart(false);
            return;
        }
        if (startVisible) {
            if (e.key === "Enter") {
                e.preventDefault();
                startRows[startIndex]?.run();
            } else if (e.key === "ArrowDown") {
                e.preventDefault();
                setPickedIndex((startIndex + 1) % startRows.length);
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setPickedIndex((startIndex - 1 + startRows.length) % startRows.length);
            }
            return;
        }
        if (e.key === "Enter" && e.shiftKey && canAsk && query) {
            e.preventDefault();
            handleAskUnsent();
            return;
        }
        if (e.key === "Enter") {
            // Before the results arrive: doing words open the first action (free),
            // a question is asked, anything else searched.
            if (!showSearchResults) {
                if (isAction) runAction(matched[0]);
                else if (canAsk && looksLikeQuestion(query)) handleAsk();
                else if (query.length >= 3) handleViewAll();
                return;
            }
            const command = commandAt(selectedIndex);
            if (command) {
                command.run();
                return;
            }
            const resultIndex = selectedIndex - resultOffset;
            if (selectedIndex >= 0 && resultIndex >= 0 && resultIndex < flatResults.length) {
                const selectedItem = flatResults[resultIndex];
                rememberResult(selectedItem);
                window.open(selectedItem.url, "_blank");
            } else {
                handleViewAll();
            }
            return;
        }
        if (!showSearchResults || totalRows === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setPickedIndex((selectedIndex + 1) % totalRows);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setPickedIndex(selectedIndex < 0 ? totalRows - 1 : (selectedIndex - 1 + totalRows) % totalRows);
        }
    };

    useEffect(() => {
        if (
            selectedIndex >= 0 &&
            resultRefs.current[selectedIndex] &&
            typeof resultRefs.current[selectedIndex]?.scrollIntoView === "function"
        ) {
            resultRefs.current[selectedIndex]!.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [selectedIndex]);

    let globalIndex = resultOffset;

    const renderResultItem = (item: SearchResultItem) => {
        const currentIndex = globalIndex++;
        return (
            <SearchResultRow
                key={`${item.type}-${item.id}-${currentIndex}`}
                item={item}
                query={searchText}
                variant="compact"
                isSelected={currentIndex === selectedIndex}
                onOpen={rememberResult}
                anchorRef={(el) => (resultRefs.current[currentIndex] = el)}
            />
        );
    };

    const renderCommand = (row: CommandRow, index: number, isSelected: boolean, onHover: () => void) => (
        <button
            key={row.key}
            type="button"
            ref={(el) => (resultRefs.current[index] = el)}
            className={`btn-unstyled ${classes.askRow} ${isSelected ? classes.askRowSelected : ""}`}
            onClick={row.run}
            onMouseEnter={onHover}
            title={row.hint}
        >
            <span className={row.kind === "do" ? classes.doTile : classes.askTile} aria-hidden="true"><i className={`bi bi-${row.icon}`} /></span>
            <span className={classes.askBody}>
                <span className={classes.askTitle}>{row.title}</span>
                <span className={classes.askQuestion}>{row.detail}</span>
            </span>
            {isSelected && <kbd className={classes.askKbd}>Enter</kbd>}
        </button>
    );

    const commandGroup = (rows: CommandRow[], offset: number, position: "first" | "last") => (rows.length > 0 ? (
        <div className={`${classes.askGroup} ${position === "first" ? classes.askGroupFirst : classes.askGroupLast}`}>
            {rows.map((row, at) => renderCommand(row, offset + at, selectedIndex === offset + at, () => setPickedIndex(offset + at)))}
        </div>
    ) : null);

    const startGroups = [
        { label: null, rows: startRows.filter((row) => row.key === "open" || row.key === "resume") },
        { label: "Things I can do for you", rows: startRows.filter((row) => row.kind === "do") },
        { label: "Ask about", rows: startRows.filter((row) => row.key.startsWith("q:")) },
    ].filter((group) => group.rows.length > 0);

    const showAskInline = canAsk && query.length > 0;

    return (
        <div className={`${classes.SearchHeaderContainer} position-relative`} ref={dropdownRef}>
            <div className={classes.searchInputWrapper}>
                <i className={`bi bi-search ${classes.searchIcon}`} aria-hidden="true" />
                <input
                    type="text"
                    value={searchText}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        if (query.length >= 3 && searchResults.length + suggestions.length > 0) setShowSearchResults(true);
                        if (canAsk && !query) {
                            setShowStart(true);
                            setPickedIndex(null);
                        }
                    }}
                    placeholder={canAsk ? "Search, or ask CRX a question…" : "Search missions, clients, people, places…"}
                    aria-label={canAsk ? "Search, or ask CRX a question" : "Search missions, clients, people and places"}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={showSearchResults || startVisible}
                    aria-controls={RESULTS_ID}
                    className={`form-control ${classes.headerSearchInput} ${showAskInline ? classes.headerSearchInputAsk : ""}`}
                />
                {showAskInline && (
                    <button type="button" className={`btn-unstyled ${classes.askInline}`} onClick={handleAsk}
                        title={`Ask CRX this question: the answer opens beside this page (${messageCost(aiAccess).toLowerCase()})`}>
                        <i className="bi bi-stars" aria-hidden="true" />
                        <span>Ask CRX</span>
                    </button>
                )}
                {!showAskInline && isLoading && <div className={classes.searchSpinner} aria-label="Searching" />}

                {/* The shortcut is advertised where the intent to search forms.
                    Clicking it opens the palette, so the hint is also the button. */}
                {!showAskInline && !isLoading && (
                    <button
                        type="button"
                        className={`btn-unstyled ${classes.searchShortcut}`}
                        title={`Open the command palette (${COMMAND_PALETTE_HINT})`}
                        aria-label={`Open the command palette (${COMMAND_PALETTE_HINT})`}
                        onClick={openCommandPalette}
                    >
                        {COMMAND_PALETTE_KEYS.map((key) => (
                            <kbd key={key}>{key}</kbd>
                        ))}
                    </button>
                )}
            </div>

            {startVisible && (
                <div className={classes.searchDropdown} id={RESULTS_ID}>
                    <div className={classes.panelBody}>
                        {startGroups.map((group) => (
                            <div key={group.label ?? "open"} className={classes.askGroup}>
                                {group.label && <span className={classes.startLabel}>{group.label}</span>}
                                {group.rows.map((row) => {
                                    const at = startRows.indexOf(row);
                                    return renderCommand(row, at, at === startIndex, () => setPickedIndex(at));
                                })}
                            </div>
                        ))}
                    </div>
                    <div className={classes.panelFooter}>
                        <span className={classes.keyHints}>
                            <kbd>↑</kbd><kbd>↓</kbd><span className={classes.keyHintLabel}>move</span>
                            <kbd>Enter</kbd><span className={classes.keyHintLabel}>open</span>
                            <kbd>Esc</kbd><span className={classes.keyHintLabel}>close</span>
                        </span>
                    </div>
                </div>
            )}

            {showSearchResults && !startVisible && (
                <div className={classes.searchDropdown} id={RESULTS_ID}>
                    <div className={classes.panelBody}>
                        {commandGroup(topRows, 0, "first")}
                        {!quietSearch && <SearchSuggestions suggestions={suggestions} onPick={pickQuery} />}
                        {!quietSearch && (
                            <SearchSummaryBanner
                                summary={summary}
                                compact
                                onPick={pickQuery}
                                onOpen={(url) => window.open(url, "_blank")}
                            />
                        )}
                        {quietSearch ? null : flatResults.length > 0 ? (
                            <>
                                {groupedResults.map((group) => (
                                    <section key={group.category} className={classes.categoryGroup} aria-label={group.config.label}>
                                        <div className={classes.categoryHeader}>
                                            <i className={`bi ${group.config.icon} ${classes.categoryIcon}`} aria-hidden="true" />
                                            <span className={classes.categoryLabel}>{group.config.label}</span>
                                            <span className={classes.categoryCount}>
                                                {group.subgroups.reduce((count, sub) => count + sub.items.length, 0)}
                                            </span>
                                        </div>
                                        {group.subgroups.map((sub) => (
                                            <React.Fragment key={sub.group ?? "all"}>
                                                {sub.group && <span className={classes.subgroupHeader}>{sub.group}</span>}
                                                <div className={classes.rows}>{sub.items.map(renderResultItem)}</div>
                                            </React.Fragment>
                                        ))}
                                    </section>
                                ))}
                                {inactiveResults.length > 0 && (
                                    <section className={classes.categoryGroup} aria-label="Inactive records">
                                        <div className={classes.categoryHeader}>
                                            <i className={`bi bi-archive ${classes.categoryIcon}`} aria-hidden="true" />
                                            <span className={classes.categoryLabel}>Inactive records</span>
                                            <span className={classes.categoryCount}>{inactiveResults.length}</span>
                                        </div>
                                        <div className={classes.rows}>{inactiveResults.map(renderResultItem)}</div>
                                    </section>
                                )}
                            </>
                        ) : (
                            <div className={classes.noResults}>
                                <i className="bi bi-search" aria-hidden="true" />
                                <span className={classes.noResultsTitle}>No matches for “{query}”</span>
                                <span className={classes.noResultsHint}>Try a name, a reference number, an email or a phone number.</span>
                            </div>
                        )}
                        {commandGroup(bottomRows, topRows.length + flatResults.length, "last")}
                    </div>

                    <div className={classes.panelFooter}>
                        <button type="button" className={`btn-unstyled ${classes.viewAll}`} onClick={handleViewAll}>
                            <i className="bi bi-arrow-return-left" aria-hidden="true" />
                            <span className={classes.viewAllLabel}>See all results for “{query}”</span>
                        </button>
                        <span className={classes.keyHints}>
                            <kbd>↑</kbd><kbd>↓</kbd><span className={classes.keyHintLabel}>move</span>
                            <kbd>Enter</kbd><span className={classes.keyHintLabel}>{canAsk ? "open or ask" : "open"}</span>
                            {canAsk && <><kbd>Shift</kbd><kbd>Enter</kbd><span className={classes.keyHintLabel}>edit in Ask</span></>}
                            <kbd>Esc</kbd><span className={classes.keyHintLabel}>close</span>
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchDropdown;
